import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import DateRangePicker from "components/date-range-picker";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { toast } from "react-toastify";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import useHistoryPanel from "hooks/shared/useHistoryPanel";
import { useAppTranslation } from "locale/useAppTranslation";
import { errorHandler } from "_services/errorHandler";
import { editVehicleOperationPageRouteName } from "../edit/edit.page";
import {
  useVehicleOperationServiceV1ListVehicleOperationsQuery,
  useVehicleOperationServiceV1ActivateVehicleOperationsMutation,
  useVehicleOperationServiceV1DeactivateVehicleOperationsMutation,
  type VehicleOperationV1VehicleOperationRes,
} from "_services/modules/vehicle_operation";

const sortFieldMap: Record<string, string> = {
  vehicleType: "vehicle_type_name",
  vehicleModel: "vehicle_model_name",
  fuelType: "fuel_type_name",
  fuelUsage: "fuel_usage",
  avgSpeed: "avg_speed",
  workingHours: "working_hours",
  status: "status",
  createdAt: "created_at",
  updatedAt: "updated_at",
};

export interface VehicleOperationRow {
  id: string;
  vehicleType: string;
  vehicleModel: string;
  fuelType: string;
  tripType: string;
  fuelUsage: number;
  avgSpeed: number;
  workingHours: number;
  status: "active" | "inactive";
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

const useVehicleOperationList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleOperation");
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const [toggleRow, setToggleRow] = useState<VehicleOperationRow | null>(null);
  const historyPanel = useHistoryPanel("vehicle_operation");

  const { data: vehicleOperationConfigsData, isLoading, refetch } = useVehicleOperationServiceV1ListVehicleOperationsQuery({
    page: currentPage,
    size: rowsPerPage,
    sort: sortState?.columnId ? (sortFieldMap[sortState.columnId] ?? sortState.columnId) : "created_at",
    order: sortState?.direction ?? "desc",
    status: filterValues.status || undefined,
    vehicleTypeName: filterValues.vehicleType || undefined,
    vehicleModelName: filterValues.vehicleModel || undefined,
    fuelTypeName: filterValues.fuelType || undefined,
    tripTypeName: filterValues.tripType || undefined,
    fuelUsage: filterValues.fuelUsage ? Number(filterValues.fuelUsage) : undefined,
    avgSpeed: filterValues.avgSpeed ? Number(filterValues.avgSpeed) : undefined,
    workingHours: filterValues.workingHours ? Number(filterValues.workingHours) : undefined,
    createdAtFrom: filterValues.createdAt?.split("|")[0] || undefined,
    createdAtTo: filterValues.createdAt?.split("|")[1] || undefined,
    updatedAtFrom: filterValues.updatedAt?.split("|")[0] || undefined,
    updatedAtTo: filterValues.updatedAt?.split("|")[1] || undefined,
  });
  const [activateVehicleOperationConfigs] = useVehicleOperationServiceV1ActivateVehicleOperationsMutation();
  const [deactivateVehicleOperationConfigs] = useVehicleOperationServiceV1DeactivateVehicleOperationsMutation();

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-list"),
        hasBackButton: false,
      })
    );
  }, [dispatch, t]);

  const tableData: VehicleOperationRow[] = useMemo(() => {
    if (!vehicleOperationConfigsData?.vehicle_operations) return [];
    return vehicleOperationConfigsData.vehicle_operations.map((item: VehicleOperationV1VehicleOperationRes) => ({
      id: item.id || "",
      vehicleType: item.vehicle_type?.name || "",
      vehicleModel: item.vehicle_model?.name || "",
      fuelType: item.fuel_type?.name || "",
      tripType: item.trip_type?.name || "",
      fuelUsage: item.fuel_usage || 0,
      avgSpeed: item.avg_speed || 0,
      workingHours: item.working_hours || 0,
      status: (item.status || "inactive") as "active" | "inactive",
      createdBy: item.created_by || "",
      createdAt: item.created_at || "",
      updatedBy: item.updated_by || "",
      updatedAt: item.updated_at || "",
    }));
  }, [vehicleOperationConfigsData]);

  const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
  const hasMixedStatus =
    selectedRows.length > 0 &&
    new Set(selectedRows.map((r) => r.status)).size > 1;

  const isBulkActionDisabled =
    selectedKeys.length === 0 || hasMixedStatus;

  const handleApplyBulkAction = () => {
    if (!bulkAction || isBulkActionDisabled) return;
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    try {
      const ids = selectedKeys.map((key) => String(key));
      if (bulkAction === "activate") {
        await activateVehicleOperationConfigs({ vehicleOperationV1VehicleOperationBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      } else if (bulkAction === "deactivate") {
        await deactivateVehicleOperationConfigs({ vehicleOperationV1VehicleOperationBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      }
      setShowBulkConfirmModal(false);
      setBulkAction("");
      setSelectedKeys([]);
      refetch();
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorHandler(error);
    }
  };

  const handleToggleStatus = (row: VehicleOperationRow) => {
    setToggleRow(row);
    setShowToggleConfirmModal(true);
  };

  const handleConfirmToggle = async () => {
    if (!toggleRow) return;
    try {
      const ids = [toggleRow.id];
      if (toggleRow.status === "active") {
        await deactivateVehicleOperationConfigs({ vehicleOperationV1VehicleOperationBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      } else {
        await activateVehicleOperationConfigs({ vehicleOperationV1VehicleOperationBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      }
      setShowToggleConfirmModal(false);
      setToggleRow(null);
      refetch();
    } catch (error) {
      console.error("Toggle failed:", error);
      errorHandler(error);
    }
  };

  const handleFilterChange = (values: Record<string, string>) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleEdit = (row: VehicleOperationRow) => {
    navigate(editVehicleOperationPageRouteName(row.id.toString()));
  };

  const bulkConfirmMessage = useMemo(() => {
    if (selectedRows.length === 0) return "";
    const status = selectedRows[0]?.status;
    return status === "active"
      ? t("bulk-confirm-deactivate")
      : t("bulk-confirm-activate");
  }, [selectedRows, t]);

  const columns: Columns<VehicleOperationRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
      },
      {
        id: "vehicleType",
        fieldId: "vehicleType",
        label: t("label-vehicle-type"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "vehicleModel",
        fieldId: "vehicleModel",
        label: t("label-vehicle-model"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "fuelType",
        fieldId: "fuelType",
        label: t("label-fuel-type"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "tripType",
        fieldId: "tripType",
        label: t("label-trip-type"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "fuelUsage",
        fieldId: "fuelUsage",
        label: t("label-fuel-usage"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-fuel-usage-${row.id}`}>{row.fuelUsage} {t("col-unit-km-l")}</span>
        ),
      },
      {
        id: "avgSpeed",
        fieldId: "avgSpeed",
        label: t("label-avg-speed"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-avg-speed-${row.id}`}>{row.avgSpeed} {t("col-unit-km-jam")}</span>
        ),
      },
      {
        id: "workingHours",
        fieldId: "workingHours",
        label: t("label-working-hours"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-working-hours-${row.id}`}>{row.workingHours} {t("col-unit-hours")}</span>
        ),
      },
      {
        id: "status",
        fieldId: "status",
        label: t("label-status"),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t("col-all-data"), value: "" },
          { label: t("status-active"), value: "active" },
          { label: t("status-inactive"), value: "inactive" },
        ],
        render: (row) => (
          <div className="inline-flex items-center gap-2 text-sm" data-testid={`col-status-${row.id}`}>
            <span
              className={`h-3 w-3 rounded-full ${
                row.status === "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
              }`}
            />
            <span className="capitalize">{row.status}</span>
          </div>
        ),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "140px",
        dragDisabled: true,
        render: (row) => (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => handleEdit(row)}
              data-testid="btn-edit-operation"
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label: row.status === "active" ? t("btn-deactivate") : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                  variant: row.status === "active" ? "danger" : "default",
                  testId: row.status === "active" ? "btn-deactivate-operation" : "btn-activate-operation",
                },
                {
                  label: t("btn-track-history"),
                  onClick: () => historyPanel.openPanel(row.id, row.vehicleType),
                  testId: "btn-track-history-operation",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  const totalPages = vehicleOperationConfigsData?.metadata?.total_page ?? 1;

  return {
    tableData,
    isLoading,
    data: tableData,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    handleFilterChange,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleConfirmToggle,
    bulkConfirmMessage,
    historyPanel,
    t,
  };
};

export default useVehicleOperationList;
